import React, { useMemo } from 'react'
import { useNavigate } from 'react-router'
import {
  ActionList,
  ActionListItemProps,
  Avatar,
  Button,
  Icon,
  useSidePanel,
} from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import {
  CustomerTypeHighlight,
  EmbeddedCustomerStatusHighlight,
  NestedCustomerStatusHighlight,
} from 'pages/Customers/components/CustomerHighlight'
import { EmbeddedCustomerDetails } from 'pages/EmbeddedCustomers/CustomerDetails/EmbeddedCustomerDetails'
import { EMBEDDED_CUSTOMER_STATUSES } from 'pages/EmbeddedCustomers/helpers/constants'
import {
  EmbeddedCustomerStatus,
  type EmbeddedCustomer,
} from 'pages/EmbeddedCustomers/helpers/types'
import { NestedCustomerDetails } from 'pages/NestedCustomers/components/NestedCustomerDetails'
import { Customer } from 'types/customers'
import { CustomerModel, CustomerModels } from '../helpers/types'

const CUSTOMER_VIEW_ENABLED_STATUSES = new Set<EmbeddedCustomerStatus>([
  EMBEDDED_CUSTOMER_STATUSES.VERIFIED,
  EMBEDDED_CUSTOMER_STATUSES.TERMINATED,
])

export interface CustomerItemsProps {
  customerModel: CustomerModel
  customers: Customer[]
}

export const CustomerItems = ({ customerModel, customers }: CustomerItemsProps) => {
  const navigate = useNavigate()

  const {
    isSidePanelOpen: isCustomerDetailsOpen,
    sidePanelData: customerDetails,
    paging: pageCustomers,
    setIsSidePanelOpen: setIsCustomerDetailsOpen,
    updateSidePanel: updateCustomerDetails,
  } = useSidePanel(customers)

  const isEmbedded = useMemo(() => customerModel === CustomerModels.EMBEDDED, [customerModel])
  const isReliance = useMemo(() => customerModel === CustomerModels.RELIANCE, [customerModel])

  const onGoToCustomer = (reference: string) => {
    void navigate(`${PathNames.CUSTOMERS}/${reference}`)
  }

  const onOpenCustomerDetails = (customerIndex: number) => {
    setIsCustomerDetailsOpen(true)
    updateCustomerDetails(customerIndex)
  }

  const onCloseCustomerDetails = () => {
    setIsCustomerDetailsOpen(false)
  }

  const customerItems: ActionListItemProps[] = useMemo(
    () =>
      customers?.map(({ reference, name = '', description = '', type, status }, index) => ({
        transparent: true,
        key: reference,
        id: reference,
        testId: `customer-${name}`,
        Avatar: <Avatar type="entity" name={name} />,
        title: name,
        HeaderHighlightLeft: <CustomerTypeHighlight type={type} />,
        ...(description && {
          BodyPre: description,
        }),
        ActionHighlight:
          isEmbedded || isReliance ? (
            <EmbeddedCustomerStatusHighlight status={status} />
          ) : (
            <NestedCustomerStatusHighlight status={status} />
          ),
        ...(CUSTOMER_VIEW_ENABLED_STATUSES.has(status) && {
          onItemClick: (e: { stopPropagation: () => void }) => {
            e?.stopPropagation()
            onGoToCustomer(reference)
          },
        }),
        CustomAction: (
          <Button
            variant="ghost"
            iconElement={<Icon name="DotsThreeVerticalCustom" />}
            onClick={() => onOpenCustomerDetails(index)}
          />
        ),
      })),
    [customers, isEmbedded]
  )

  return (
    <>
      <ActionList id={`${customerModel?.toLowerCase()}-customers`} items={customerItems} />
      {isEmbedded || isReliance ? (
        <EmbeddedCustomerDetails
          isOpen={isCustomerDetailsOpen}
          customerDetails={customerDetails as EmbeddedCustomer} // NOSONAR – CustomerStatusType is a subset of EmbeddedCustomerStatus, safe to widen
          pageCustomers={pageCustomers}
          onClose={onCloseCustomerDetails}
          isReliance={isReliance}
        />
      ) : (
        <NestedCustomerDetails
          isOpen={isCustomerDetailsOpen}
          customerDetails={customerDetails as EmbeddedCustomer} // NOSONAR – CustomerStatusType is a subset of EmbeddedCustomerStatus, safe to widen
          pageCustomers={pageCustomers}
          onClose={onCloseCustomerDetails}
        />
      )}
    </>
  )
}
